import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["getRawValue", "inputRef", "maskOptions", "onChange"];
import React, { forwardRef, useEffect, useRef } from 'react';
import { shape, func, any } from 'prop-types';
import IMask from 'imask';
import { useForkRef } from '@material-ui/core/utils';
import Input from '../input/Input';
import CustomDateMask from '../date-input/CustomDateMask';
class CustomIMask extends IMask.InputMask {
  // This override should extend the maskEquals conditional for CustomDateMask case
  // The original method includes the following expresion:
  // mask === Date && this.masked instanceof MaskedDate
  maskEquals(mask) {
    return super.maskEquals(mask) || mask === CustomDateMask && this.masked instanceof IMask.MaskedDate;
  }
}
const MaskedInput = /*#__PURE__*/forwardRef(function MaskedInput(props, ref) {
  const {
      getRawValue,
      inputRef: inputRefProp,
      maskOptions,
      onChange
    } = props,
    otherProps = _objectWithoutProperties(props, _excluded);
  const inputRef = useRef(null);
  const maskInstanceRef = useRef(null);

  // handle our inputRef and the one from the props in a single one
  const handleInputRef = useForkRef(inputRef, inputRefProp);

  // create the instance of the IMask
  useEffect(function () {
    if (!maskInstanceRef.current && inputRef.current) {
      maskInstanceRef.current = new CustomIMask(inputRef.current, maskOptions);
      maskInstanceRef.current.on('accept', function () {
        if (onChange) {
          const event = {
            currentTarget: inputRef.current,
            target: inputRef.current,
            type: 'change'
          };
          event.target.value = maskInstanceRef.current.value;
          event.target.rawValue = getRawValue(maskInstanceRef.current);
          onChange(event);
        }
      });
    }
    return function () {
      if (maskInstanceRef.current) {
        maskInstanceRef.current.off('accept');
        maskInstanceRef.current.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // update the mask if the options change
  useEffect(function () {
    if (maskInstanceRef.current) {
      maskInstanceRef.current.updateOptions(maskOptions);
    }
  }, [maskOptions]);
  return /*#__PURE__*/React.createElement(Input, Object.assign({}, otherProps, {
    ref: ref,
    inputRef: handleInputRef
  }));
});
MaskedInput.defaultProps = {
  getRawValue: function (mask) {
    return mask.unmaskedValue;
  }
};
MaskedInput.propTypes = {
  /**
   * A function to extract the raw value from the mask, by default it is using
   * the `unmaskedValue` prop from the IMask instance.
   *
   * The raw value is attached to the event.target.
   *
   * It is useful in case that representation of the value is not useful for your component.
   */
  getRawValue: func,
  /**
   * @ignore
   */
  inputRef: any,
  /**
   * Refer to [IMask documentation](https://imask.js.org/guide.html#masked).
   */
  maskOptions: shape({
    mask: any.isRequired
  }).isRequired,
  /**
   * Callback fired when the value is changed.
   *
   * __Note:__ The event is not a real DOM event, it is an Object that contains
   * `currentTarget`, `target` and `type` properties to fake it. This is because
   * the underlying library updates the input value manually and when you do that
   * the browsers do not trigger a change event.
   *
   * @param {object} event The event source of the callback.
   */
  onChange: func
};
export default MaskedInput;